import { Link, Navigate, Outlet, useLocation } from 'react-router-dom';
import { AppShell, Chip, Logo, Spinner, type NavItem } from '@nexvia-logistics/ui';
import { useAuth } from '@/shared/useAuth';
import { activeNavIdFor, NAV_ITEMS } from '@/shared/nav-items';
import { useNavCounts } from '@/shared/useNavCounts';
import { useStorageProfileGate } from '@/shared/useStorageProfileGate';
import { useStorageModuleState } from '@/shared/useStorageModuleState';
import StorageModuleOffPage from '@/shared/StorageModuleOffPage';
import { loginPathFor } from '@/shared/return-to';
import { LockersIcon, NAV_ICON_BY_ID, QuestionIcon } from '@/shared/icons';

function LoadingScreen() {
  return (
    <div className="grid min-h-dvh place-items-center bg-surface-container">
      <Spinner size={32} label="Cargando tu sesión…" />
    </div>
  );
}

/**
 * The route-level wrapper every protected page nests under: resolves the auth state, redirects to
 * `/login` when there is none, and otherwise renders the real `@nexvia-logistics/ui` `AppShell`
 * around the matched child route — the same composition nexvia-backoffice's `AdminLayout.tsx`
 * makes over the same template. NEX-1164 (2026-10-15): this used to wrap a hand-rolled stand-in
 * (`src/shared/AppShell.tsx`, deleted with this change) that mirrored the template's props; the
 * real one brings the badge pills, the bell, the `ProfileMenu`, the sticky sidebar, the sliding
 * drawer and the collapsible rail with the isotype (`Logo variant="mark"`) for free.
 *
 * The shell is router-agnostic, so this file supplies the link element — and sets `aria-current`
 * from `renderLink`'s 4th argument, which a supplied link does not get for free (see the
 * template's own docblock). The real `NavItem` carries its icon, so it is built here from the
 * router-facing `NAV_ITEMS` plus `useNavCounts`'s live badge counts.
 */
export default function AppLayout() {
  const location = useLocation();
  const { user, loading, isAuthenticated, logout } = useAuth();
  // Called unconditionally, above the early returns below, per React's Rules of Hooks — `enabled`
  // is what actually keeps this from fetching before a session is confirmed (see useNavCounts.ts).
  // NEX-1164 — an email + password login whose email is not verified yet is refused by the API
  // (`AUTH_EMAIL_NOT_VERIFIED`): it asks nothing and goes to /registro's verification step.
  const emailUnverified = user?.emailVerified === false;
  // NEX-1645 — the module's state (`GET /app/features`, public): `null` while it loads, `full` when
  // the read fails ([ASSUMED], see the hook). With `off` the shell asks the API nothing.
  const moduleState = useStorageModuleState();
  const moduleOpen = moduleState !== null && moduleState !== 'off';
  const counts = useNavCounts(isAuthenticated && !loading && !emailUnverified && moduleOpen);
  // NEX-1164 — a restored session with no depósito account goes to /registro (see the hook).
  const profileMissing = useStorageProfileGate(isAuthenticated && !loading && !emailUnverified && moduleOpen);

  if (loading) {
    return <LoadingScreen />;
  }

  if (!isAuthenticated) {
    // NEX-1165 — keep where the visit was going (e.g. the mail's /solicitudes?requestId=<id>), so
    // Login.tsx returns there after signing in.
    return <Navigate to={loginPathFor(location.pathname, location.search)} replace />;
  }

  if (moduleState === null) {
    return <LoadingScreen />;
  }

  // NEX-1645 — module turned off from the backoffice: a session already open sees the notice full
  // screen instead of the panel (Login and /registro show the same one), and can sign out.
  if (moduleState === 'off') {
    return <StorageModuleOffPage onSignOut={() => void logout()} />;
  }

  if (profileMissing || emailUnverified) {
    return <Navigate to="/registro" replace />;
  }

  const items: NavItem[] = NAV_ITEMS.map((item) => {
    const Icon = NAV_ICON_BY_ID[item.id];
    return { ...item, icon: Icon ? <Icon size={20} /> : null, badge: counts[item.id] };
  });

  return (
    <AppShell
      groups={[{ id: 'main', items }]}
      activeId={activeNavIdFor(location.pathname)}
      logo={<Logo height={22} />}
      logoMark={<Logo variant="mark" height={28} />}
      collapsible
      user={{ name: user?.email ?? '' }}
      onSignOut={logout}
      // Audit S7 — no `notifications`: the package `AppShell` draws the bell only when given one,
      // and the depósito role has no notifications feed (no data source, endpoint or UI); a bell
      // that did nothing on click was hidden. The mail is how a depósito hears of a new request.
      sidebarHeader={
        <Chip tone="info" icon={<LockersIcon size={14} />}>
          Panel del depósito
        </Chip>
      }
      topbarActions={
        // `md:`, not `sm:` — the preset declares only md/lg/xl, so `sm:inline` kept this label
        // permanently hidden (spec finding 3). The host is read live (audit STO-7): the panel is
        // served from storage.nexvia.io (storage.dev|staging.nexvia.io before that), never from
        // the `depositos.nexvia.uy` this label used to hardcode.
        <span className="hidden text-ui-small text-on-surface-placeholder md:inline">{window.location.host}</span>
      }
      sidebarFooter={
        <div className="flex flex-col gap-2">
          {/* Storage feedback (2026-10-26): no "Ver la app del usuario" link — a depósito has no use for the
              client web app. Same address nexvia-web-app's own src/layout/AppLayout.tsx sidebarFooter already
              uses for this exact link ("Ayuda y soporte" → mailto:soporte@nexvia.io) — reused, not
              invented, see icons.tsx's own comment on `QuestionIcon`. */}
          <a
            href="mailto:soporte@nexvia.io"
            className="inline-flex items-center gap-2 text-ui-label text-on-surface-variant no-underline hover:text-on-surface"
          >
            <QuestionIcon size={18} />
            Ayuda y soporte
          </a>
        </div>
      }
      renderLink={(item, content, className, state) => (
        <Link to={item.href} className={className} aria-current={state.current ? 'page' : undefined}>
          {content}
        </Link>
      )}
    >
      <Outlet />
    </AppShell>
  );
}
